import type { ModelAssetNode } from '../assets/model-asset-types'

/**
 * 首次加载整棵项目模型树的计划输入。
 */
export interface ProjectModelRootLoadPlanInput {
  /**
   * 当前项目模型根节点。
   */
  readonly root: ModelAssetNode
  /**
   * 加载计划类型：首次整树加载。
   */
  readonly kind: 'root-load'
}

/**
 * 替换既有子树时的加载计划输入。
 */
export interface ProjectModelReplaceSubtreeLoadPlanInput {
  /**
   * 当前项目模型根节点。
   */
  readonly root: ModelAssetNode
  /**
   * 加载计划类型：替换目标子树。
   */
  readonly kind: 'replace-subtree'
  /**
   * 被替换子树的根节点 key。
   */
  readonly targetNodeKey: string
  /**
   * 目标子树的父节点 key，用于限定重组范围。
   */
  readonly parentNodeKey: string
}

/**
 * 插入新节点时的加载计划输入。
 */
export interface ProjectModelInsertNodeLoadPlanInput {
  /**
   * 当前项目模型根节点。
   */
  readonly root: ModelAssetNode
  /**
   * 加载计划类型：插入单个节点。
   */
  readonly kind: 'insert-node'
  /**
   * 已经挂入树中的新增节点 key。
   */
  readonly insertedNodeKey: string
  /**
   * 新增节点的父节点 key，用于限定重组范围。
   */
  readonly parentNodeKey: string
}

/**
 * 项目模型加载计划输入。
 */
export type ProjectModelLoadPlanInput =
  | ProjectModelRootLoadPlanInput
  | ProjectModelReplaceSubtreeLoadPlanInput
  | ProjectModelInsertNodeLoadPlanInput

/**
 * 项目模型重组范围。
 */
export type ProjectModelRecompositionScope =
  | {
    /**
     * 重组整棵模型树。
     */
    readonly kind: 'whole-tree'
  }
  | {
    /**
     * 仅重组父节点对应的子树。
     */
    readonly kind: 'parent-subtree'
    /**
     * 需要重组的父节点 key。
     */
    readonly parentNodeKey: string
  }

/**
 * 项目模型加载计划。
 */
export interface ProjectModelLoadPlan {
  /**
   * 本次需要下载和解析的节点，顺序必须与加载事务的读取顺序一致。
   */
  readonly nodesToLoad: readonly ModelAssetNode[]
  /**
   * 本次加载完成后需要重组的模型范围。
   */
  readonly recompositionScope: ProjectModelRecompositionScope
}

/**
 * 按前序收集节点，保证父装配先于子零件进入加载事务。
 *
 * @param node 当前递归入口节点。
 * @returns 从当前节点开始的前序节点列表。
 */
function collectNodesPreorder(node: ModelAssetNode): readonly ModelAssetNode[] {
  return [
    node,
    ...node.children.flatMap((child) => collectNodesPreorder(child)),
  ]
}

/**
 * 在模型树中查找指定节点。
 *
 * @param root 当前模型树根节点。
 * @param nodeKey 需要定位的节点 key。
 * @returns 与 nodeKey 匹配的模型资产节点。
 * @throws 当模型树中不存在该节点时抛出包含 nodeKey 的错误，便于调用方定位坏输入。
 */
function findModelAssetNode(root: ModelAssetNode, nodeKey: string): ModelAssetNode {
  const found = collectNodesPreorder(root).find((node) => node.nodeKey === nodeKey)

  if (!found) {
    throw new Error(`项目模型节点不存在：nodeKey=${nodeKey}`)
  }

  return found
}

/**
 * 为项目模型生成加载计划。
 *
 * @param input 加载计划输入，描述首次加载、子树替换或节点插入场景。
 * @returns cad-core 加载事务可消费的节点列表和重组范围。
 */
export function createProjectModelLoadPlan(input: ProjectModelLoadPlanInput): ProjectModelLoadPlan {
  if (input.kind === 'root-load') {
    return {
      nodesToLoad: collectNodesPreorder(input.root),
      recompositionScope: { kind: 'whole-tree' },
    }
  }

  if (input.kind === 'insert-node') {
    return {
      nodesToLoad: collectNodesPreorder(findModelAssetNode(input.root, input.insertedNodeKey)),
      recompositionScope: {
        kind: 'parent-subtree',
        parentNodeKey: input.parentNodeKey,
      },
    }
  }

  return {
    nodesToLoad: collectNodesPreorder(findModelAssetNode(input.root, input.targetNodeKey)),
    recompositionScope: {
      kind: 'parent-subtree',
      parentNodeKey: input.parentNodeKey,
    },
  }
}
